import type {
  RestoreAccount,
  GcpBigQueryResourceTablesInner,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { dayjs } from '@/utils/dayjs';

import { BigQueryConfiguration } from './steps/bigQueryConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreBigQueryState {
  resourceId: string;
  snapshot?: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  targetDatasetId: string;
  targetLocation: string;
  availableTables: GcpBigQueryResourceTablesInner[];
  selectedTables: string[];
  isNativePitr: boolean;
  isPiTR: boolean;
  restoreToTime?: Date;
  providerAccountId: string;
  sourceRegion: string;
  sourceProvider: Provider;
  resource?: InventoryResource;
}

const restoreBigQueryFlow: Step<RestoreBigQueryState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'big_query_configuration',
      Component: BigQueryConfiguration,
    },
  ],
};

const formatDateTimeForDatasetId = (): string => {
  return dayjs().utc().format('YYYYMMDDHHmmss');
};

export const RestoreBigQueryWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
  restoreToTime,
}: {
  resource: InventoryResource;
  snapshot?: Snapshot;
  onClose: () => void;
  onBack: () => void;
  restoreToTime?: Date;
}) => {
  const isNativePitr = !snapshot;
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;
  const originalDatasetId = bigQueryProps?.datasetId || '';
  const originalLocation = bigQueryProps?.location || '';
  const availableTables = bigQueryProps?.tables || [];
  const defaultTargetDatasetId = originalDatasetId
    ? `${originalDatasetId}_${formatDateTimeForDatasetId()}`
    : '';

  return (
    <Wizard<RestoreBigQueryState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreBigQueryFlow]}
      initialState={{
        snapshot,
        resourceId: resource.id,
        supportedCloudProviders: [Provider.Gcp],
        targetDatasetId: defaultTargetDatasetId,
        targetLocation: originalLocation,
        availableTables,
        selectedTables: [],
        isNativePitr,
        isPiTR: isNativePitr,
        restoreToTime,
        providerAccountId: resource.providerAccountId,
        sourceRegion: resource.region,
        sourceProvider: resource.cloudProvider,
        resource,
      }}
      title='Restore BigQuery'
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
